import type { Capability, ScopeRef } from "@enduring-systems/entitlements";

import { apiClient, ApiError } from "@/lib/api-client";
import type { IdentitySuccess, RelayedRequest, RelayedSuccess } from "@/lib/identity-contract";

export type GrantMutationResult = RelayedSuccess<"setEntitlementGrant">;

/** A grant applies at the base tier or as an override on top of it. */
export type GrantTier = GrantMutationResult["tier"];

/** How far one write reaches, as the admin-api classifies it. */
export type BlastRadius = GrantMutationResult["blastRadius"];

/**
 * `value` is the RAW candidate value (a number, boolean, string, or JSON
 * object), not the tagged `EntitlementValue`: the editor unwraps the control's
 * tagged value before calling `setGrant`.
 */
export type SetGrantInput = RelayedRequest<"setEntitlementGrant">;

export type ClearGrantInput = RelayedRequest<"clearEntitlementGrant">;

export type BundleMutationResult = RelayedSuccess<"applyEntitlementBundle">;

/** One capability a bundle operation moved, with what it was and what it became. */
export type BundleChange = BundleMutationResult["changes"][number];

export type ApplyBundleInput = RelayedRequest<"applyEntitlementBundle">;

export type RetractBundleInput = RelayedRequest<"retractEntitlementBundle">;

/**
 * Apply a versioned bundle at a scope, writing its member grants as
 * bundle-sourced rows. Overrides at the same scope keep precedence over them.
 */
export async function applyBundle(input: ApplyBundleInput): Promise<BundleMutationResult> {
  return apiClient.put<RelayedSuccess<"applyEntitlementBundle">>(
    "/identity/entitlements/bundle-applications",
    input
  );
}

/**
 * Retract a bundle from a scope. NO version is sent, and none is accepted: the
 * service removes every version of the named bundle, so that a caller cannot
 * half-retract by naming a version that is not the applied one.
 */
export async function retractBundle(input: RetractBundleInput): Promise<BundleMutationResult> {
  return apiClient.del<RelayedSuccess<"retractEntitlementBundle">>(
    "/identity/entitlements/bundle-applications",
    input
  );
}

/** The capability registry (definitions only) for the picker + control rendering. */
export async function getRegistry(): Promise<readonly Capability[]> {
  const response = await apiClient.get<IdentitySuccess<"getEntitlementsRegistry">>(
    "/identity/entitlements/registry"
  );
  return response.capabilities;
}

/** Set (or override) a capability's grant value at a scope. */
export async function setGrant(input: SetGrantInput): Promise<GrantMutationResult> {
  return apiClient.put<RelayedSuccess<"setEntitlementGrant">>(
    "/identity/entitlements/grants",
    input
  );
}

/** Clear a grant at a scope, falling back to the inherited/default value. */
export async function clearGrant(input: ClearGrantInput): Promise<GrantMutationResult> {
  return apiClient.del<RelayedSuccess<"clearEntitlementGrant">>(
    "/identity/entitlements/grants",
    input
  );
}

/**
 * Effective-vs-granted for one capability at one scope. The effective `value`
 * is whatever the resolver relays, untouched: any JSON value, never narrowed
 * to an `EntitlementValue`.
 */
export type EffectiveVsGranted = RelayedSuccess<"readEffectiveEntitlement">;

export type EffectiveView = EffectiveVsGranted["effective"];

export type EffectiveSourceView = Extract<EffectiveView, { resolved: true }>["source"];

/** What is set AT the selected scope, with no resolution applied. */
export type GrantedAtScope = NonNullable<EffectiveVsGranted["granted"]>;

export type ReadEffectiveInput = RelayedRequest<"readEffectiveEntitlement">;

/**
 * Read effective-vs-granted for a scope + capability. The BFF derives the ancestor
 * chain itself, so no principal is sent: naming a scope is the whole request.
 */
export async function readEffective(input: ReadEffectiveInput): Promise<EffectiveVsGranted> {
  return apiClient.post<RelayedSuccess<"readEffectiveEntitlement">>(
    "/identity/entitlements/effective",
    input
  );
}

export type AuditLogPage = RelayedSuccess<"readEntitlementAuditLog">;

/** One recorded mutation: who changed what at this scope, and when. */
export type AuditLogEntry = AuditLogPage["entries"][number];

/** A stored audit value: the raw value with the type that was in force. */
export type AuditValueView = NonNullable<AuditLogEntry["before"]>;

export type ReadAuditLogInput = RelayedRequest<"readEntitlementAuditLog">;

/**
 * Read a page of the mutation audit for a scope, newest first.
 *
 * Anchored to the scope, never across tenants: the service computes the partition
 * from the scope named here, so there is no form of this request that reads
 * another tenant's history.
 *
 * A page narrowed by capability can come back SHORT or empty while the cursor
 * still points at more rows, because the narrowing is applied after the page is
 * read. Callers page until the cursor is null rather than until a page is short.
 */
export async function readAuditLog(input: ReadAuditLogInput): Promise<AuditLogPage> {
  return apiClient.post<RelayedSuccess<"readEntitlementAuditLog">>(
    "/identity/entitlements/audit-log",
    input
  );
}

/**
 * Everything at ONE scope as a set. Scope-local by design: a capability showing
 * nothing in force here may still resolve from an ancestor, which this read
 * deliberately does not walk.
 */
export type ScopeStateView = RelayedSuccess<"readEntitlementScopeState">;

export type ScopeStateCapability = ScopeStateView["capabilities"][number];

/** One stored row at the scope, with the provenance its `source` records. */
export type StoredGrantView = NonNullable<ScopeStateCapability["base"]>;

/**
 * What the resolution path does with one stored row. `ignored` and `denying`
 * rows are not in force, so a surface must never draw them as if they were.
 */
export type EngineRowState = StoredGrantView["engineState"];

/** What is in force AT this scope, or why nothing is. Never a bare value. */
export type EffectiveAtScope = ScopeStateCapability["effective"];

export type AppliedBundle = ScopeStateView["appliedBundles"][number];

/**
 * Read everything set at one scope. The BFF derives nothing from the caller:
 * naming the scope is the whole request, and the partition is computed from it.
 */
export async function readScopeState(scope: ScopeRef): Promise<ScopeStateView> {
  const body: RelayedRequest<"readEntitlementScopeState"> = { scope };
  return apiClient.post<RelayedSuccess<"readEntitlementScopeState">>(
    "/identity/entitlements/scope-state",
    body
  );
}

/**
 * One catalog entry, presentation only. Whether this offering is applied
 * anywhere comes from `readScopeState`, never from here.
 */
export type OfferingCatalogEntry = RelayedSuccess<"listEntitlementOfferings">["offerings"][number];

/** Display-only price metadata, in the currency's minor units. */
export type OfferingPrice = NonNullable<OfferingCatalogEntry["price"]>;

/** The code-governed offering catalog, for the console's presentation. */
export async function getOfferings(): Promise<readonly OfferingCatalogEntry[]> {
  const response = await apiClient.get<RelayedSuccess<"listEntitlementOfferings">>(
    "/identity/entitlements/offerings"
  );
  return response.offerings;
}

type WireRequestQueue = RelayedSuccess<"readEntitlementRequestQueue">;

/** One queue entry: the request plus its age as the service read it. */
export type PendingRequestView = WireRequestQueue["requests"][number];

/** What a requester asked for, always in offering terms. */
export type RequestedChange = PendingRequestView["change"];

/** The offering a request names, at the version the requester was shown. */
export type RequestedOfferingRef = RequestedChange["offering"];

export type RequestStatus = PendingRequestView["status"];

/**
 * Who asked, as the staff plane resolved them. "We looked and found nobody" is
 * distinguishable from a name, and an unresolved requester is a value a
 * surface renders rather than an absence it guesses at.
 */
export type RequesterIdentity = PendingRequestView["requester"];

/**
 * `MAX_QUEUE_LIMIT` in `services/entitlements-admin-api/src/request-store.ts`.
 *
 * Sent explicitly rather than left to the service default of 50, because this
 * page filters a platform-wide queue down to one scope: the more rows the read
 * returns, the narrower the window in which the cap can hide a request
 * belonging to the workspace on screen.
 */
export const REQUEST_QUEUE_LIMIT = 100;

/**
 * The queue, plus the age (seconds) at which the queue-age alarm fires as the
 * deployed stack configured it, or null when it injected none. Null is not a
 * licence to substitute a default: flags drawn against an invented number
 * would read as the alarm being accounted for when it is not.
 */
export type RequestQueueView = WireRequestQueue;

/**
 * The pending request queue (ENR-1947), platform-wide.
 *
 * There is deliberately no per-scope form of this read: the store's pending
 * index is keyed for the CS queue rather than per tenant. A caller that wants
 * one workspace's requests filters this list, and must treat a full page as a
 * FLOOR on that workspace's count rather than the count itself.
 */
export async function readRequestQueue(
  limit: number = REQUEST_QUEUE_LIMIT
): Promise<RequestQueueView> {
  const body: RelayedRequest<"readEntitlementRequestQueue"> = { limit };
  const response = await apiClient.post<WireRequestQueue>(
    "/identity/entitlements/request-queue",
    body
  );
  return {
    /* The contract requires both fields, but the console deploys apart from
       the service it reads, so an absent requester still reads as unresolved
       and an absent threshold as none. */
    requests: response.requests.map((request) => ({
      ...request,
      requester: request.requester ?? { resolved: false },
    })),
    /* Coerced to null rather than trusted: a non-positive value could only
       flag every row. */
    maxAgeSeconds:
      typeof response.maxAgeSeconds === "number" && response.maxAgeSeconds > 0
        ? response.maxAgeSeconds
        : null,
  };
}

/** Which way a queued request was answered. */
export type RequestDecision = "approve" | "deny";

export type ApproveRequestInput = RelayedRequest<"approveEntitlementRequest">;

/** `reason` is shown verbatim to the requester, so it is customer-visible copy. */
export type DenyRequestInput = RelayedRequest<"denyEntitlementRequest">;

/** An approval is a bundle apply, so it reports the apply it performed. */
export type ApproveRequestResult = RelayedSuccess<"approveEntitlementRequest">;

/** The request as it stands after a decision. */
export type DecidedRequestView = ApproveRequestResult["request"];

/**
 * Approve a queued request, which applies the offering it names in the same
 * transaction (ENR-1947). There is no approve-without-apply: a request that
 * reached `approved` with nothing applied is the state this endpoint exists to
 * make unreachable.
 */
export async function approveRequest(input: ApproveRequestInput): Promise<ApproveRequestResult> {
  return apiClient.post<RelayedSuccess<"approveEntitlementRequest">>(
    "/identity/entitlements/request-approvals",
    input
  );
}

/** Deny a queued request with the reason the requester will be shown. */
export async function denyRequest(input: DenyRequestInput): Promise<DecidedRequestView> {
  const response = await apiClient.post<RelayedSuccess<"denyEntitlementRequest">>(
    "/identity/entitlements/request-denials",
    input
  );
  return response.request;
}

/**
 * The peer decision that beat this caller, read off a typed 409.
 *
 * Null for anything else, INCLUDING a 409 that is not this code: a concurrent
 * grant write also answers 409, and reporting that as "already handled" would
 * tell an operator their request was decided when it is still pending.
 */
export function alreadyDecided(
  cause: unknown
): { readonly status: RequestStatus | null; readonly decidedBy: string | null } | null {
  if (!(cause instanceof ApiError)) return null;
  if (cause.code !== "REQUEST_ALREADY_DECIDED") return null;
  const status = cause.details?.["status"];
  const decidedBy = cause.details?.["decidedBy"];
  return {
    status: isRequestStatus(status) ? status : null,
    decidedBy: typeof decidedBy === "string" && decidedBy !== "" ? decidedBy : null,
  };
}

const REQUEST_STATUSES: readonly RequestStatus[] = ["pending", "approved", "applied", "denied"];

function isRequestStatus(raw: unknown): raw is RequestStatus {
  return (REQUEST_STATUSES as readonly unknown[]).includes(raw);
}
